import { useEffect, useMemo, useRef } from 'react';

import { Chip, Panel, PanelHeader, StatLabel } from './panel';
import { MiniSegmentedControl } from './mini-segmented-control';
import {
  DEFENSIVE_CONTRIBUTION_BASIS_OPTIONS,
  DEFENSIVE_CONTRIBUTION_STANDING_LABEL,
  getDefensiveContributionStanding,
  summariseDefensiveContribution,
  type DefensiveContributionBasis,
  type DefensiveContributionGameweek
} from '@lib/page-utils/player-profile/metrics';
import {
  formatInteger,
  formatNumber,
  formatPercentage,
  NO_VALUE
} from '@lib/page-utils/player-profile/format';

const CHART_HEIGHT_PX = 148;
/** Headroom above the threshold so a bar on the line is not flush to the top. */
const SCALE_HEADROOM = 2;
/**
 * Below this width the per-gameweek labels overlap into noise, so the chart
 * scrolls horizontally rather than squeezing further.
 */
const MINIMUM_COLUMN_WIDTH_PX = 34;

export type { DefensiveContributionGameweek };

type DefensiveContributionProps = {
  /** CBIT for defenders, CBIT+R for midfielders and forwards. */
  metricLabel: string;
  threshold: number;
  ruleDescription: string;
  /** Actions on the current basis, e.g. per 90 minutes. */
  actionsOnBasis: number | null;
  /** Points banked across the selected window, not a per-match rate. */
  pointsBanked: number | null;
  gameweeks: DefensiveContributionGameweek[];
  basisLabel: string;
  hitRateBasis: DefensiveContributionBasis;
  onHitRateBasisChange: (basis: DefensiveContributionBasis) => void;
};

const standingTone = (hitRate: number) => {
  const standing = getDefensiveContributionStanding(hitRate);
  if (standing === 'reliable') return 'success' as const;
  if (standing === 'occasional') return 'warning' as const;
  return 'neutral' as const;
};

export const DefensiveContribution = ({
  metricLabel,
  threshold,
  ruleDescription,
  actionsOnBasis,
  pointsBanked,
  gameweeks,
  basisLabel,
  hitRateBasis,
  onHitRateBasisChange
}: DefensiveContributionProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // The most recent gameweeks are the interesting end, so land there rather
  // than at gameweek one whenever the window changes.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollLeft = container.scrollWidth;
  }, [gameweeks]);

  const summary = useMemo(
    () =>
      summariseDefensiveContribution({
        gameweeks,
        threshold,
        basis: hitRateBasis
      }),
    [gameweeks, threshold, hitRateBasis]
  );

  const { hitRate, clearedCount, countedCount, hasSmallSample } = summary;
  const countedNoun = hitRateBasis === 'starts' ? 'starts' : 'appearances';

  const chartScale = Math.max(
    threshold + SCALE_HEADROOM,
    ...gameweeks.map((gameweek) => gameweek.actions)
  );

  const gaugeWidth =
    actionsOnBasis === null
      ? 0
      : Math.min((actionsOnBasis / threshold) * 100, 100);

  const isCounted = (gameweek: DefensiveContributionGameweek) =>
    hitRateBasis === 'starts' ? gameweek.didStart : gameweek.hasAppeared;

  return (
    <Panel>
      <PanelHeader
        title="Defensive contribution"
        subtitle={ruleDescription}
        action={
          // A standing chip off a handful of matches would read as a verdict
          // the sample cannot support, so it is withheld until the rate is
          // measured over enough of them.
          hitRate !== null && !hasSmallSample ? (
            <Chip tone={standingTone(hitRate)}>
              {
                DEFENSIVE_CONTRIBUTION_STANDING_LABEL[
                  getDefensiveContributionStanding(hitRate)
                ]
              }
            </Chip>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 border-t border-neutral-200 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div className="flex flex-col gap-5 border-b border-neutral-200 px-5 py-5 sm:px-6 lg:border-b-0 lg:border-r">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <StatLabel>Hit rate</StatLabel>
              <MiniSegmentedControl
                ariaLabel="Hit rate measured over"
                options={DEFENSIVE_CONTRIBUTION_BASIS_OPTIONS}
                value={hitRateBasis}
                onChange={onHitRateBasisChange}
              />
            </div>
            <p className="mt-1 font-sans text-[44px] font-bold tabular-nums leading-none text-neutral-900">
              {formatPercentage(hitRate)}
            </p>
            <p className="mt-1.5 text-[13px] text-neutral-500">
              {countedCount === 0
                ? `No ${countedNoun} in this window`
                : `${clearedCount} of ${countedCount} ${countedNoun} cleared the threshold`}
            </p>
            {hasSmallSample && (
              <p className="mt-1.5 text-[13px] text-warning-700">
                Too few {countedNoun} to read as a rate yet.
              </p>
            )}
          </div>

          <div>
            <StatLabel>
              {metricLabel} {basisLabel}
            </StatLabel>
            <p className="mt-1 flex items-baseline gap-1.5">
              <span className="font-sans text-[22px] font-bold tabular-nums text-neutral-900">
                {formatNumber(actionsOnBasis, 2)}
              </span>
              <span className="text-[13px] text-neutral-500">
                / {threshold} needed
              </span>
            </p>
            <div className="mt-2 h-2 rounded-full bg-neutral-100">
              <div
                className="h-full rounded-full bg-warning-400"
                style={{ width: `${gaugeWidth}%` }}
              />
            </div>
          </div>

          <div>
            <StatLabel>Points banked</StatLabel>
            <p className="mt-1 font-sans text-[22px] font-bold tabular-nums text-neutral-900">
              {formatInteger(pointsBanked)}
            </p>
            <p className="mt-1 text-[13px] text-neutral-500">
              Total across this window
            </p>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <StatLabel>Qualifying actions by gameweek</StatLabel>
            <span className="flex items-center gap-2 text-[11px] text-neutral-500">
              <span
                aria-hidden
                className="inline-block h-0 w-5 border-t-2 border-dashed border-danger-600"
              />
              {threshold} threshold
            </span>
          </div>

          <div ref={scrollContainerRef} className="overflow-x-auto">
            <div
              className="relative flex items-end gap-2"
              style={{
                height: CHART_HEIGHT_PX,
                minWidth: gameweeks.length * MINIMUM_COLUMN_WIDTH_PX
              }}
            >
              <span
                aria-hidden
                className="absolute inset-x-0 border-t-2 border-dashed border-danger-600"
                style={{ bottom: `${(threshold / chartScale) * 100}%` }}
              />
              {gameweeks.map((gameweek) => {
                const isInBasis = isCounted(gameweek);
                const hasCleared = isInBasis && gameweek.actions >= threshold;
                // Three states, because two would let an excluded cameo read
                // as a failed start: cleared, a counted miss, and a match the
                // current basis does not count at all.
                const barTone = hasCleared
                  ? 'bg-success-500'
                  : isInBasis
                  ? 'bg-neutral-300'
                  : 'bg-neutral-200';
                return (
                  <div
                    key={gameweek.gameweek}
                    className="flex h-full min-w-0 flex-1 flex-col justify-end"
                  >
                    <div
                      className={`mx-auto w-[68%] rounded-t-[3px] ${barTone}`}
                      style={{
                        height: gameweek.hasAppeared
                          ? `${(gameweek.actions / chartScale) * 100}%`
                          : '0%'
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <div
              className="flex gap-2 pt-2"
              style={{
                minWidth: gameweeks.length * MINIMUM_COLUMN_WIDTH_PX
              }}
            >
              {gameweeks.map((gameweek) => (
                <div
                  key={gameweek.gameweek}
                  className="flex min-w-0 flex-1 flex-col items-center"
                >
                  <span
                    className={`text-[11px] font-bold tabular-nums ${
                      isCounted(gameweek)
                        ? 'text-neutral-800'
                        : 'text-neutral-400'
                    }`}
                  >
                    {gameweek.hasAppeared
                      ? formatInteger(gameweek.actions)
                      : NO_VALUE}
                  </span>
                  <span className="text-[10px] text-neutral-400">
                    GW{gameweek.gameweek}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
};
